import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AuthLayout from './auth/AuthLayout';
import AuthForm from './auth/AuthForm';
import { API_BASE_URL } from '../App';
import { validatePassword } from '../lib/emailValidation';
import './LoginPage.css';

function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setError('');
    setStatus(null);

    if (!token) {
      setError('This reset link is invalid. Request a new one from the forgot password page.');
      return;
    }

    const pwCheck = validatePassword(password, { confirm: confirmPassword });
    if (!pwCheck.ok) {
      setFieldErrors({ password: pwCheck.error });
      return;
    }
    setFieldErrors({});

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/sessions/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Could not reset password');
        return;
      }
      setStatus({
        type: 'ok',
        message: data.message || 'Password updated. You can sign in now.',
      });
    } catch (e) {
      setError(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Choose a new password"
      subtitle="Enter a new password for your Plate Chase account"
    >
      {!token && (
        <p className="auth-form__error" role="alert">
          This reset link is missing or invalid.{' '}
          <Link to="/forgot-password">Request a new link</Link>.
        </p>
      )}

      {token && !status && (
        <AuthForm
          fields={[
            {
              id: 'reset-password',
              name: 'password',
              label: 'New password',
              type: 'password',
              value: password,
              onChange: setPassword,
              autoComplete: 'new-password',
              error: fieldErrors.password,
              disabled: loading,
            },
            {
              id: 'reset-confirm',
              name: 'confirmPassword',
              label: 'Confirm new password',
              type: 'password',
              value: confirmPassword,
              onChange: setConfirmPassword,
              autoComplete: 'new-password',
              disabled: loading,
            },
          ]}
          onSubmit={handleSubmit}
          submitLabel="Update password"
          loading={loading}
          error={error}
        />
      )}

      {status && (
        <p className="auth-form__status auth-form__status--ok" role="status">
          {status.message}
        </p>
      )}

      <p className="auth-form__links">
        <Link to="/login">Back to sign in</Link>
      </p>
    </AuthLayout>
  );
}

export default ResetPasswordPage;
